// @ts-nocheck
import type {
  CartDiscountsV2,
  CartDiscountsV2PromotionTypeEnum,
} from '@redteclab/api/clients/bully'
import { arrayOf, string } from 'prop-types'
import { type FunctionComponent } from 'react'
import { useSelector } from 'react-redux'

import { SA_SELLER_ID } from 'shared/consts/checkout'
import { PRESCRIPTION_TYPES } from 'shared/consts/productTemplates'
import { TENANT_AT, TENANT_CH, TENANT_COM } from 'shared/consts/tenants'
import {
  CartControllerCartShippingDetailV2,
  CartControllerSellerGroupV2,
  CartOverdraftsV2,
} from 'types/api/cart'
import { type IState, type NowDataState } from 'types/redux'
import NowNeutralCaseBox from 'views/components/molecules/NowNeutralCaseBox'
import NowWithDeliveryTimeCaseBox from 'views/components/molecules/NowWithDeliveryTimeCaseBox'
import ProfesionalCustomerNotificationBlock from 'views/components/organisms/Cart/ProfesionalCustomerNotificationBlock'
import { isMixedGroupSelector } from 'views/components/organisms/Cart/selectors'
import SellerModal from 'views/components/organisms/SellerModal'
import Tenant from 'views/components/utils/Tenant'
import CswWithHydration from 'views/containers/spa/Cart/CswWithHydration'
import {
  cartOverdrafts,
  cartShippingDetail,
  sellerGroup,
} from 'views/utils/propTypes'

import ProductListCartPerSeller from './ProductListCartPerSeller'
import ProductListOverdrafts from './ProductListOverdrafts'
import { SellerPromotionBanner } from './SellerPromotionBanner/SellerPromotionBanner'
import SellerShippingAddressBox from './SellerShippingAddressBox'

import './ProductListCart.scss'

const MIN_COUNT_TO_SHOW_NOW_MESSAGE = 3

const getSellerRelatedPromotionData = (sellerDiscounts: CartDiscountsV2[]) => {
  const promotionData: Record<CartDiscountsV2PromotionTypeEnum, string[]> = {}

  sellerDiscounts.forEach(discountInstance => {
    if (discountInstance.promotionType) {
      if (!promotionData[discountInstance.promotionType]) {
        promotionData[discountInstance.promotionType] = []
      }
      promotionData[discountInstance.promotionType].push(
        ...discountInstance.affectedProductIds
      )
    }
  })

  return promotionData
}

interface IProductListCartProps {
  sellerGroups: CartControllerSellerGroupV2[]
  shippingDetail: CartControllerCartShippingDetailV2[]
  currency: string
  overdrafts: CartOverdraftsV2[]
  showNowCases: boolean
}

const ProductListCart: FunctionComponent<IProductListCartProps> = ({
  sellerGroups,
  shippingDetail,
  currency,
  overdrafts,
  showNowCases,
}) => {
  const sellerCount = sellerGroups.length
  const hasOverdrafts = overdrafts && overdrafts.length > 0
  const nowData: NowDataState = useSelector((state: IState) => state.nowData)
  const isMixedGroup = useSelector(isMixedGroupSelector)
  const zipCode = nowData?.zipCode
  const cartInitialState = useSelector(
    (state: IState) => state.componentInitialState.CartComponentInitialState
  )
  const isFreeShipping = shippingDetail[0]?.shippingCost === 0
  const isEligibleNowMessage = (seller: CartControllerSellerGroupV2) => {
    if (seller.sellerId !== SA_SELLER_ID) {
      return false
    }

    const noPrescriptionCount = seller.groupEntries
      .filter(
        groupEntry => groupEntry.type === PRESCRIPTION_TYPES.NO_PRESCRIPTION
      )
      .reduce((acc, groupEntry) => acc + groupEntry.items.length, 0)

    const totalCount =
      noPrescriptionCount +
      seller.groupEntriesERx.reduce(
        (acc, groupEntryErx) => acc + (groupEntryErx.items || []).length,
        0
      )

    return totalCount >= MIN_COUNT_TO_SHOW_NOW_MESSAGE
  }

  return (
    <>
      {sellerGroups.map((seller, idx) => (
        <div
          key={`ProductListCart-${seller.sellerName}-${seller.sellerGroupId}`}
        >
          {(!hasOverdrafts ||
            sellerCount > 1 ||
            seller.sellerId !== SA_SELLER_ID) && (
            <SellerShippingAddressBox
              idx={idx}
              seller={seller.sellerName}
              sellerId={seller.sellerId}
              total={sellerCount}
              sellerGroups={sellerGroups}
              shippingDetail={shippingDetail}
              currency={currency}
            />
          )}

          {seller.sellerPromotionV2 && (
            <SellerPromotionBanner
              sellerPromotion={seller.sellerPromotionV2}
              sellerName={seller.sellerName}
              sellerId={seller.sellerId}
            />
          )}

          <ProfesionalCustomerNotificationBlock sellerGroup={seller} />

          {isMixedGroup && showNowCases && isEligibleNowMessage(seller) && (
            <section className="o-ProductListCart__nowCasesContainer u-margin-large--bottom">
              {zipCode ? <NowWithDeliveryTimeCaseBox /> : <NowNeutralCaseBox />}
            </section>
          )}

          {hasOverdrafts && seller.sellerId === SA_SELLER_ID && (
            <ProductListOverdrafts
              sellerGroups={sellerGroups}
              overdrafts={overdrafts}
            />
          )}
          <div className="cart-product-list-wrapper">
            <ProductListCartPerSeller
              currency={currency}
              productTypes={seller.groupEntries}
              cartShippingDetail={shippingDetail?.find(
                shippingOption => shippingOption.sellerId === seller.sellerId
              )}
              eRxEntries={seller.groupEntriesERx}
              seller={seller.sellerName}
              sellerId={seller.sellerId}
              sellerGroupId={seller.sellerGroupId}
              hasOverdrafts={hasOverdrafts && seller.sellerId === SA_SELLER_ID}
              sellerPromotions={getSellerRelatedPromotionData(seller.discounts)}
              sellerPromotion={seller.sellerPromotionV2}
            />
          </div>
          <Tenant tenant={[TENANT_COM, TENANT_CH, TENANT_AT]}>
            {!cartInitialState?.hideCartSuggestionCsw && (
              <CswWithHydration isFreeShipping={isFreeShipping} />
            )}
          </Tenant>
        </div>
      ))}
      <SellerModal />
    </>
  )
}

ProductListCart.propTypes = {
  sellerGroups: arrayOf(sellerGroup).isRequired,
  shippingDetail: arrayOf(cartShippingDetail).isRequired,
  overdrafts: arrayOf(cartOverdrafts).isRequired,
  currency: string.isRequired,
}

export default ProductListCart
